import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import toast from "react-hot-toast";
import { Save, Send } from "react-feather";
import {
  useGetSettingsQuery,
  useSetSettingMutation,
  useTestEmailSettingMutation,
  useTestWhatsAppSettingMutation,
  useTestSmsSettingMutation,
  useTestServiceEmailSettingMutation,
} from "../settingsApi";
import { useGetProductGroupsQuery } from "../../masters/products/productsApi";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";

// Product_Group_Id is branch-local (handoff §9.7) - a raw numeric text box
// forces whoever's setting this to already know the ID by heart, which
// nobody does. Swapped for a searchable-by-name picker scoped to whichever
// branch is currently selected in the header, same groups list Product
// Groups/Stock filters already fetch and filter client-side (no working
// branchId filter server-side, per productsApi.js's own comment).
const GROUP_PICKER_KEY = "Vehicles.SparesProductGroupId";

const GROUP_TITLES = {
  Company: "Company (Quotation Letterhead)",
  Email: "Email (SMTP)",
  WhatsApp: "WhatsApp (Twilio)",
  Sms: "SMS (SMS Leopard)",
  Technical: "Technical (Customer Service Emails)",
  PettyCash: "Petty Cash",
  Vehicles: "Vehicles",
  Amc: "AMC (Service & Renewal Reminders)",
};
// A group missing from this list renders NOTHING, however many settings the
// server has in it - which is exactly what happened to Amc: Batch136 seeded
// Amc.ServiceLogMinimumGapDays months before anyone could see or change it.
// Whenever a batch seeds a setting in a new group, add the group here too.
const GROUP_ORDER = ["Company", "Email", "WhatsApp", "Sms", "Technical", "PettyCash", "Vehicles", "Amc"];

// Groups that are deliberately GLOBAL, whatever the header branch selector says.
//
// Email and WhatsApp are the opposite - the server REQUIRES a branch for those
// (SystemSettingsService.BranchRequiredGroups), because each branch has its own
// mailbox and Twilio number. There is one SMS Leopard account for the whole
// company, so Sms is global.
//
// Without this, the page's normal behaviour (save to whichever branch is selected)
// silently wrote the SMS credentials to BranchId 1 while the sender read BranchId 0,
// and the test button reported "SMS is not configured" with everything apparently
// filled in. Found 2026-09-18 during the first on-prem test.
const GLOBAL_ONLY_GROUPS = new Set(["Sms"]);

// Settings whose value is a whole number of days. A plain text box invites
// "two weeks" or "45 days", neither of which TRY_CAST(... AS INT) survives -
// the proc would silently fall back to its default and nobody would know the
// setting had not taken. A number input with a days suffix says what is wanted.
const DAY_COUNT_KEYS = new Set([
  "Amc.ServiceLogMinimumGapDays",
  "Amc.ServiceDueLeadDays",
  "Amc.ServiceOverdueCriticalDays",
  "Amc.RenewalReminderLeadDays",
]);

// "Smtp.FromAddress" -> "From Address"
const keyLabel = (settingKey) =>
  settingKey
    .split(".")
    .pop()
    .replace(/([a-z])([A-Z])/g, "$1 $2");

// One setting = one row, its own local edit state seeded once from the
// server value (secrets always start blank - the server never returns a
// real secret value, only "***configured***"/null, and re-sending that
// mask string back would overwrite the real secret). Local state instead
// of a shared parent object keyed by settingKey deliberately, so saving one
// row (which refetches the whole list, handoff §18) never clobbers
// whatever another row's input currently has typed into it.
//
// `hasValue` folds together the global case (a real value/mask is present)
// and the per-branch case (an override row exists for this branch at all,
// handoff §18.1) - a branch with no override shows "Not set for this
// branch" and starts blank, same as an unset secret, rather than silently
// falling back to the global value.
const SettingRow = ({ setting, hasValue, scopeLabel, onSave, saving, groupOptions }) => {
  const isSecret = setting.isSecret;
  const [value, setValue] = useState(isSecret || !hasValue ? "" : setting.value ?? "");
  // The branch picker changes `hasValue`/`setting.value` (the per-branch
  // override query resolving after this row already mounted) without ever
  // changing this row's own React `key` - so the useState initializer above
  // only ever captured whatever was known at that first, possibly-premature
  // render (often still empty/loading). Re-sync whenever the real
  // value/scope actually changes so switching branches (or the initial
  // network fetch landing late) doesn't leave the input stuck blank while
  // the "Configured" badge above it - which recomputes fresh every render -
  // already shows correctly. Depends on the primitive setting.value/hasValue,
  // not the setting object itself, so a whole-list refetch triggered by
  // *another* row's save (same settingKey, unchanged value) doesn't refire
  // this and clobber whatever the user is still typing into this one.
  useEffect(() => {
    setValue(isSecret || !hasValue ? "" : setting.value ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setting.value, hasValue, isSecret]);
  const isUseSsl = setting.settingKey === "Smtp.UseSsl";
  const isGroupPicker = setting.settingKey === GROUP_PICKER_KEY;
  const isDayCount = DAY_COUNT_KEYS.has(setting.settingKey);
  const configured = isSecret ? setting.value === "***configured***" : hasValue;

  const handleSave = async () => {
    if (!value.trim()) {
      toast.error("Enter a value first - settings can't be cleared back to blank, only overwritten");
      return;
    }
    // Caught here rather than at the server, which stores any string happily and
    // only fails to read it back much later, on a screen nobody connects to this one.
    if (isDayCount && !/^\d+$/.test(value.trim())) {
      toast.error("Enter a whole number of days, digits only");
      return;
    }
    const ok = await onSave(setting.settingKey, value.trim());
    if (ok && isSecret) setValue("");
  };

  return (
    <div className="row align-items-center mb-3">
      <div className="col-md-3">
        <label className="form-label mb-0">{keyLabel(setting.settingKey)}</label>
        <div>
          <span className={`badge ${configured ? "badge-linesuccess" : "text-bg-secondary"}`}>
            {configured ? "Configured" : `Not set${scopeLabel ? ` for ${scopeLabel}` : ""}`}
          </span>
        </div>
        {setting.description && <div className="text-muted small">{setting.description}</div>}
      </div>
      <div className="col-md-6">
        {isUseSsl ? (
          <select className="form-control" value={value} onChange={(e) => setValue(e.target.value)}>
            <option value="">--Select--</option>
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        ) : isGroupPicker ? (
          groupOptions.length > 0 ? (
            <Select
              classNamePrefix="react-select"
              options={groupOptions}
              value={groupOptions.find((o) => String(o.value) === value) || null}
              onChange={(opt) => setValue(opt ? String(opt.value) : "")}
              placeholder="Search product group..."
              isClearable
            />
          ) : (
            <div className="text-muted small">Select a branch above to search its product groups.</div>
          )
        ) : isDayCount ? (
          <div className="input-group">
            <input
              type="number"
              min="0"
              step="1"
              className="form-control"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
            <span className="input-group-text">days</span>
          </div>
        ) : (
          <input
            type={isSecret ? "password" : "text"}
            className="form-control"
            value={value}
            placeholder={isSecret ? (configured ? "Leave blank to keep current value" : "Not set") : ""}
            onChange={(e) => setValue(e.target.value)}
          />
        )}
      </div>
      <div className="col-md-3">
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleSave} disabled={saving}>
          <Save size={14} className="me-1" /> Save
        </button>
      </div>
    </div>
  );
};

// Generic admin-configurable settings (handoff §18) - SMTP/WhatsApp
// credentials for Sales quotation delivery, company letterhead details, the
// Petty Cash self-pay ceiling, and (added 2026-08-07) each branch's Vehicle
// Maintenance spares product group, all sharing one key/value store.
// Settings.ManageIntegrations-gated end to end (route + every call here) -
// SYSTEM_ADMIN only, no partial read-only view exists since even GET
// requires the same permission.
//
// Per-branch overrides (handoff §18.1) reuse the same header BranchPicker
// every other master screen uses, rather than a second scope selector on
// this page - "All branches" in the header means Global settings here (the
// same `selectedBranchId === null` the API's own branchId=0 default already
// means), picking a specific branch switches every row to that branch's own
// override, editing independently of the global value. Verified live: a
// branch with no override for a key returns no row for it at all, so rows
// are always driven off the GLOBAL list (the stable "every known key"
// catalog) with the branch-scoped list only overlaid on top when selected.
const SettingsPage = () => {
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const { data: globalSettings = [], isLoading: isLoadingGlobal } = useGetSettingsQuery(undefined);
  const { data: branchSettings = [], isLoading: isLoadingBranch } = useGetSettingsQuery(selectedBranchId, {
    skip: !selectedBranchId,
  });
  const { data: allGroups = [] } = useGetProductGroupsQuery();
  const groupOptions = useMemo(
    () =>
      selectedBranchId
        ? allGroups
            .filter((g) => g.branchId === selectedBranchId)
            .map((g) => ({ value: g.groupId, label: g.groupName }))
        : [],
    [allGroups, selectedBranchId]
  );
  const [setSetting, { isLoading: isSaving }] = useSetSettingMutation();
  const [testEmail, { isLoading: isTestingEmail }] = useTestEmailSettingMutation();
  const [testWhatsApp, { isLoading: isTestingWhatsApp }] = useTestWhatsAppSettingMutation();
  const [testSms, { isLoading: isTestingSms }] = useTestSmsSettingMutation();
  const [testServiceEmail, { isLoading: isTestingServiceEmail }] = useTestServiceEmailSettingMutation();

  const [testAddress, setTestAddress] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [testSmsPhone, setTestSmsPhone] = useState("");
  const [serviceEmailTo, setServiceEmailTo] = useState("");
  const [serviceEmailStage, setServiceEmailStage] = useState("COMPLETED");

  const isLoading = isLoadingGlobal || (Boolean(selectedBranchId) && isLoadingBranch);
  const scopeBranch = selectedBranchId
    ? availableBranches.find((b) => b.branchId === selectedBranchId)
    : null;
  const scopeLabel = scopeBranch?.branchCode || null;

  // Every row is driven off the global key catalog (stable, always all
  // known keys); when a branch is selected, overlay that branch's own
  // override row (by settingKey) in place of the global one, or mark it
  // unset if no override exists yet for this branch.
  const effectiveRows = useMemo(
    () =>
      globalSettings.map((globalRow) => {
        if (!selectedBranchId) return { setting: globalRow, hasValue: globalRow.value != null };
        // A global-only group always shows its global row, so the value on screen is
        // the one the sender will actually read - never a per-branch shadow of it.
        if (GLOBAL_ONLY_GROUPS.has(globalRow.settingGroup))
          return { setting: globalRow, hasValue: globalRow.value != null };
        const override = branchSettings.find((b) => b.settingKey === globalRow.settingKey);
        return {
          setting: override || { ...globalRow, value: null },
          hasValue: Boolean(override),
        };
      }),
    [globalSettings, branchSettings, selectedBranchId]
  );

  const grouped = useMemo(() => {
    const map = {};
    effectiveRows.forEach((row) => {
      const group = row.setting.settingGroup;
      if (!map[group]) map[group] = [];
      map[group].push(row);
    });
    return map;
  }, [effectiveRows]);

  const handleSave = async (key, value) => {
    // Group comes from the global catalog, which always carries every known key -
    // safe even when a branch is selected and the on-screen row is an override.
    const group = globalSettings.find((s) => s.settingKey === key)?.settingGroup;
    const branchId = GLOBAL_ONLY_GROUPS.has(group) ? undefined : selectedBranchId || undefined;

    try {
      await setSetting({ key, value, branchId }).unwrap();
      toast.success("Setting saved");
      return true;
    } catch (error) {
      toast.error(error?.message || "Could not save setting");
      return false;
    }
  };

  const handleTestEmail = async () => {
    if (!testAddress.trim()) {
      toast.error("Enter an address to send the test to");
      return;
    }
    try {
      await testEmail(testAddress.trim()).unwrap();
      toast.success("Test email sent");
    } catch (error) {
      toast.error(error?.message || "Test email failed");
    }
  };

  const handleTestWhatsApp = async () => {
    if (!testPhone.trim()) {
      toast.error("Enter a phone number to send the test to");
      return;
    }
    try {
      await testWhatsApp(testPhone.trim()).unwrap();
      toast.success("Test WhatsApp message sent");
    } catch (error) {
      toast.error(error?.message || "Test WhatsApp message failed");
    }
  };

  /*
    Sends a real, billed SMS - and is currently the only thing in the system that
    sends one at all.

    Its own phone state, not testPhone: that box belongs to the WhatsApp block and
    wants a +254… number, while this one accepts the local 07xx form the customer
    master is actually full of (the server normalises it).

    The error toast is given a long duration deliberately. When the provider refuses
    the message the server returns its raw response, which is how the exact request
    format gets confirmed - a toast that vanishes in two seconds would throw away
    the one piece of information this button exists to surface.
  */
  /*
    Previews a real customer service email.

    Branch-scoped deliberately, unlike the SMS test: the letterhead comes from that
    branch's Company.* values, and Systems trades as a different legal entity from
    Texas Alarms while sharing this exact template - so "which branch" is the whole
    point of the check.
  */
  const handleTestServiceEmail = async () => {
    if (!serviceEmailTo.trim()) {
      toast.error("Enter an address to send the preview to");
      return;
    }
    try {
      const result = await testServiceEmail({
        toAddress: serviceEmailTo.trim(),
        branchId: selectedBranchId || 0,
        stage: serviceEmailStage,
        sourceType: "SERVICE_VISIT",
      }).unwrap();
      toast.success(result?.message || "Preview sent");
    } catch (error) {
      toast.error(error?.message || "Preview failed", { duration: 10000 });
    }
  };

  const handleTestSms = async () => {
    if (!testSmsPhone.trim()) {
      toast.error("Enter a phone number to send the test to");
      return;
    }
    try {
      const result = await testSms(testSmsPhone.trim()).unwrap();
      toast.success(result?.message || "Test SMS sent");
    } catch (error) {
      toast.error(error?.message || "Test SMS failed", { duration: 12000 });
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>System Settings</h4>
              <h6>Email, WhatsApp, company letterhead, and other integration parameters</h6>
            </div>
          </div>
        </div>

        <div className="alert alert-info py-2">
          {scopeLabel
            ? `Editing overrides for ${scopeLabel} - use the branch picker in the header to switch branches or return to Global.`
            : "Editing Global defaults - select a branch in the header to set a branch-specific override instead."}
        </div>

        {isLoading && <p className="text-muted">Loading...</p>}

        {GROUP_ORDER.filter((g) => grouped[g]).map((group) => (
          <div className="card mb-4" key={group}>
            <div className="card-body">
              <h5 className="mb-3">{GROUP_TITLES[group] || group}</h5>
              {grouped[group].map(({ setting, hasValue }) => (
                <SettingRow
                  key={`${setting.settingKey}-${selectedBranchId ?? "global"}`}
                  setting={setting}
                  hasValue={hasValue}
                  scopeLabel={scopeLabel}
                  onSave={handleSave}
                  saving={isSaving}
                  groupOptions={groupOptions}
                />
              ))}

              {group === "Email" && (
                <div className="row align-items-center mt-2 pt-3 border-top">
                  <div className="col-md-3">
                    <label className="form-label mb-0">Send Test Email</label>
                  </div>
                  <div className="col-md-6">
                    <input
                      type="email"
                      className="form-control"
                      placeholder="someone@example.com"
                      value={testAddress}
                      onChange={(e) => setTestAddress(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleTestEmail}
                      disabled={isTestingEmail}
                    >
                      <Send size={14} className="me-1" /> Send Test
                    </button>
                  </div>
                </div>
              )}

              {group === "Technical" && (
                <div className="mt-2 pt-3 border-top">
                  <div className="row align-items-center">
                    <div className="col-md-3">
                      <label className="form-label mb-0">Preview Service Email</label>
                      <div className="text-muted small">
                        Sends a real email using this branch&apos;s Company details and the contacts above.
                      </div>
                    </div>
                    <div className="col-md-4">
                      <input
                        type="email"
                        className="form-control"
                        placeholder="someone@example.com"
                        value={serviceEmailTo}
                        onChange={(e) => setServiceEmailTo(e.target.value)}
                      />
                    </div>
                    <div className="col-md-3">
                      <select
                        className="form-control"
                        value={serviceEmailStage}
                        onChange={(e) => setServiceEmailStage(e.target.value)}
                      >
                        <option value="COMPLETED">Completed (with buttons)</option>
                        <option value="ASSIGNED">Service registered</option>
                        <option value="ONGOING">In progress</option>
                        <option value="ON_HOLD">On hold</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={handleTestServiceEmail}
                        disabled={isTestingServiceEmail}
                      >
                        <Send size={14} className="me-1" /> {isTestingServiceEmail ? "Sending..." : "Send"}
                      </button>
                    </div>
                  </div>
                  <div className="text-muted small mt-2">
                    The letterhead comes from the <strong>Company</strong> section for the selected branch, so each
                    trading entity signs its own emails. Select a branch above to preview that branch&apos;s version.
                  </div>
                </div>
              )}

              {group === "Sms" && (
                <div className="mt-2 pt-3 border-top">
                  <div className="row align-items-center">
                    <div className="col-md-3">
                      <label className="form-label mb-0">Send Test SMS</label>
                      {/* Says out loud that this costs money - the email and
                          WhatsApp tests above are effectively free, so the habit
                          this page builds is "click it and see". */}
                      <div className="text-muted small">Sends one real, billed SMS.</div>
                    </div>
                    <div className="col-md-6">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="0712345678 or 254712345678"
                        value={testSmsPhone}
                        onChange={(e) => setTestSmsPhone(e.target.value)}
                      />
                    </div>
                    <div className="col-md-3">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={handleTestSms}
                        disabled={isTestingSms}
                      >
                        <Send size={14} className="me-1" /> {isTestingSms ? "Sending..." : "Send Test"}
                      </button>
                    </div>
                  </div>
                  <div className="text-muted small mt-2">
                    These credentials are shared by every branch, so they are saved globally -
                    the branch selector above does not apply to them.
                  </div>
                </div>
              )}

              {group === "WhatsApp" && (
                <div className="row align-items-center mt-2 pt-3 border-top">
                  <div className="col-md-3">
                    <label className="form-label mb-0">Send Test WhatsApp</label>
                  </div>
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="+254700000000"
                      value={testPhone}
                      onChange={(e) => setTestPhone(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleTestWhatsApp}
                      disabled={isTestingWhatsApp}
                    >
                      <Send size={14} className="me-1" /> Send Test
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SettingsPage;
